import type { ImgHTMLAttributes } from 'react';

import { cn } from '@workspace/ui/lib/utils';

import type { CloudflareResizeOptions } from '~/components/CloudflareResponsiveImage/CloudflareResizeOptions';
import { FALLBACK_IMAGE_WIDTH } from '~/components/CloudflareResponsiveImage/constants';
import {
  getCloudflareDomain,
  getCloudflareUrl,
} from '~/components/CloudflareResponsiveImage/utils';

import type {
  CategoryBlockBackgroundImageProps,
  CategoryBlockBackgroundSource,
} from './interfaces';

/** Width at which the desktop variant takes over, matching the `md` breakpoint. */
const DEFAULT_DESKTOP_MIN_WIDTH = 768;

/**
 * Options for one candidate.
 *
 * `fit: 'cover'` is not optional. Without a `fit`, `getImgproxyUrl` falls back
 * to `rs:fit:W:H`, which bounds the image inside the box instead of filling it
 * — for a landscape source in a portrait box the requested height then has no
 * effect at all, and the browser scales the too-short result up by an arbitrary
 * fractional factor. That is the whole defect this component exists to fix.
 */
const candidateOptions = (
  width: number,
  aspectRatio: number,
  gravity?: CloudflareResizeOptions['gravity'],
): CloudflareResizeOptions => ({
  fit: 'cover',
  width,
  height: Math.round(width / aspectRatio),
  gravity,
});

/**
 * Builds the `srcset` candidate list for one variant.
 *
 * imgproxy does not enlarge, so a candidate taller than the source comes back
 * shorter than asked for. That caps achievable sharpness at the source's own
 * height rather than producing a broken URL.
 */
const buildSrcSet = (
  source: CategoryBlockBackgroundSource,
  domain: string | undefined,
): string =>
  source.widths
    .map(
      (width) =>
        `${getCloudflareUrl(
          source.src,
          domain,
          candidateOptions(width, source.aspectRatio, source.gravity),
        )} ${width}w`,
    )
    .join(', ');

/**
 * Decorative background image for a category block, sized to the box the layout
 * reserves rather than to its width alone.
 *
 * Rendered as an image instead of a CSS background on purpose: `srcset` plus
 * `sizes` lets the browser pick a candidate for the real box width times the
 * real device pixel ratio, which a background cannot do, and it keeps the
 * markup identical on the server and the client because no density is read at
 * render time. It also makes the fetch lazy, which a CSS background never is.
 *
 * When a block reserves a differently-shaped box per breakpoint, pass `desktop`
 * as well and the variants are emitted as a `<picture>`. The alternative —
 * choosing the ratio in JavaScript — is wrong on the first paint, because the
 * viewport width is not known until after mount. That wrapper is forced to
 * `block`: a `picture` is inline by default, which would leave the image's
 * `height: 100%` resolving against an auto-height box and collapse the fill.
 *
 * `fetchpriority` is spelled lowercase because React 18 does not recognise the
 * camelCase prop and forwards it to the DOM verbatim, emitting a warning.
 *
 * `decoding="async"` is left off the priority image on purpose: it permits a
 * frame to be presented before the image has decoded, which defers the very
 * LCP entry that `eager` and `fetchpriority` are there to pull forward.
 *
 * Tenant density flags are not consulted here, unlike in `cfSrcForImage` where
 * Heimkaup opts into `dpr: 2`. On a `srcset` the browser already multiplies the
 * box width by the real device ratio when it picks a candidate, so applying a
 * tenant multiplier on top would count density twice.
 */
export const CategoryBlockBackgroundImage: React.FC<
  CategoryBlockBackgroundImageProps
> = ({
  mobile,
  desktop,
  desktopMinWidth = DEFAULT_DESKTOP_MIN_WIDTH,
  priority = false,
  className,
  imageClassName,
}) => {
  const domain = getCloudflareDomain();

  const fetchPriorityAttr = priority
    ? ({
        fetchpriority: 'high',
      } as unknown as ImgHTMLAttributes<HTMLImageElement>)
    : undefined;

  const image = (
    <img
      src={getCloudflareUrl(
        mobile.src,
        domain,
        candidateOptions(
          mobile.widths[0] ?? FALLBACK_IMAGE_WIDTH,
          mobile.aspectRatio,
          mobile.gravity,
        ),
      )}
      srcSet={buildSrcSet(mobile, domain)}
      sizes={mobile.sizes}
      alt=""
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? undefined : 'async'}
      {...fetchPriorityAttr}
      className={cn(
        'size-full object-cover',
        imageClassName,
        desktop ? undefined : className,
      )}
    />
  );

  if (!desktop) {
    return image;
  }

  return (
    <picture className={cn('block size-full', className)}>
      <source
        media={`(min-width: ${desktopMinWidth}px)`}
        srcSet={buildSrcSet(desktop, domain)}
        sizes={desktop.sizes}
      />
      {image}
    </picture>
  );
};
